import { Link } from "react-router-dom";
 import { Facebook, Phone, MapPin, ArrowUp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo-official.png";
import { cn } from "@/lib/utils";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative z-50 bg-hero-premium text-primary-foreground">
      {/* Brand motif overlay */}
      <div className="absolute inset-0 hero-brand-motif opacity-30 pointer-events-none" />

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-secondary/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      {/* Scroll to Top Button */}
      <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-10">
        <Button
          onClick={scrollToTop}
          size="icon"
          className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-12 w-12"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      </div>

      <div className="container relative pt-10 pb-6 md:pt-16 md:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12">
          {/* About */}
          <div className="space-y-4">
            <Link to="/" className="inline-block group">
              <div className="flex items-center gap-3">
                <img 
                  src={logo} 
                  alt="Back to The Roots Flag Football" 
                  width={96}
                  height={96}
                  className="h-24 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </Link>
            <p className="text-sm text-primary-foreground/70 leading-relaxed">
              Building teamwork, sportsmanship, and healthy lifestyles through youth flag football in Bad Axe, Michigan.
            </p>
            
            {/* Social Icons */}
            <div className="flex gap-3 pt-2">
              <SocialIcon href="https://www.facebook.com/people/Back-to-the-Roots-Flag-Football/61570247034750/" icon={Facebook} label="Facebook" />
            </div>
          </div>

          {/* Menu - 3-column horizontal layout */}
          <div className="hidden lg:block">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-secondary" />
              Menu
            </h3>
            <ul className="grid grid-cols-3 gap-x-6 gap-y-2">
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/schedules">Schedules</FooterLink>
              <FooterLink to="/get-involved/coach">Coach</FooterLink>
              
              <FooterLink to="/my-team">My Team</FooterLink>
              <FooterLink to="/standings">Standings</FooterLink>
              <FooterLink to="/get-involved/volunteer">Volunteer</FooterLink>
              
              <FooterLink to="/schedules">Fall Season</FooterLink>
              <FooterLink to="/gallery">Gallery</FooterLink>
              <FooterLink to="/get-involved/official">Official</FooterLink>
              
              <FooterLink to="/benefits">Benefits</FooterLink>
              <FooterLink to="/league-rules">League Rules</FooterLink>
              <FooterLink to="/get-involved/sponsor">Sponsor</FooterLink>

              <FooterLink to="/resources/how-to-play">How to Play</FooterLink>
              <FooterLink to="/flag-football-bad-axe">Bad Axe</FooterLink>
              <FooterLink to="/sponsors/all-time">All-Time Sponsors</FooterLink>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-sm md:text-lg mb-2 md:mb-4 flex items-center gap-2">
              <span className="w-4 md:w-8 h-0.5 bg-secondary" />
              Contact
            </h3>
            <div className="space-y-2 md:space-y-3 text-xs md:text-sm text-primary-foreground/70">
              <div className="flex items-start gap-2 group">
                <MapPin className="h-3.5 w-3.5 mt-0.5 text-secondary/70 flex-shrink-0" />
                <span className="group-hover:text-primary-foreground/90 transition-colors">
                  Youth Sports Complex, Bad Axe, MI 48413
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-secondary/70 flex-shrink-0" />
                <div className="flex flex-wrap gap-x-3">
                  <a
                    href="tel:989-551-7473"
                    className="hover:text-secondary transition-colors"
                  >
                    (989) 551-7473
                  </a>
                  <a
                    href="tel:989-751-7869"
                    className="hover:text-secondary transition-colors"
                  >
                    (989) 751-7869
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative my-6 md:my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-primary-foreground/10" />
          </div>
          <div className="relative flex justify-center">
            <div className="bg-primary px-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-secondary rotate-45" />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="text-sm text-primary-foreground/60 space-y-3 md:space-y-4">
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-6">
              <Link 
                to="/privacy-policy" 
                className="hover:text-secondary transition-colors relative group"
              >
                Privacy Policy
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-secondary transition-all group-hover:w-full" />
              </Link>
              <Link 
                to="/terms-of-service" 
                className="hover:text-secondary transition-colors relative group"
              >
                Terms of Service
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-secondary transition-all group-hover:w-full" />
              </Link>
              <Link 
                to="/admin/standings" 
                className="hover:text-secondary transition-colors relative group"
              >
                Admin
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-secondary transition-all group-hover:w-full" />
              </Link>
          </div>
          
          {/* Valentine Digital Marketing Credit */}
           <div className="text-center">
             <a 
               href="https://valentinedigitalmarketing.com" 
               target="_blank" 
               rel="noopener noreferrer"
               className="inline-flex items-center gap-2 group"
             >
               <Sparkles className="h-3 w-3 text-secondary/50 group-hover:text-secondary transition-colors duration-300" />
               <span className="text-xs md:text-sm text-primary-foreground/50 tracking-wide uppercase">
                 Crafted by
               </span>
               <span className="text-xs md:text-sm text-secondary font-semibold tracking-wider uppercase group-hover:text-secondary/80 transition-colors duration-300">
                 Valentine Digital
               </span>
               <Sparkles className="h-3 w-3 text-secondary/50 group-hover:text-secondary transition-colors duration-300" />
             </a>
           </div>
          
          {/* Copyright - At very bottom */}
          <p className="text-center text-xs text-primary-foreground/50">
            © {currentYear} Back to The Roots Flag Football. Made in Michigan.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Social Icon Component
const SocialIcon = ({ 
  href, 
  icon: Icon, 
  label 
}: { 
  href: string; 
  icon: React.ElementType; 
  label: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={cn(
      "p-2.5 rounded-full transition-all duration-300",
      "bg-primary-foreground/10 hover:bg-secondary hover:text-secondary-foreground",
      "hover:scale-110 hover:-translate-y-1 hover:shadow-lg"
    )}
    aria-label={label}
  >
    <Icon className="h-4 w-4" />
  </a>
);

// Footer Link Component
const FooterLink = ({ 
  to, 
  children 
}: { 
  to: string; 
  children: React.ReactNode;
}) => (
  <li>
    <Link 
      to={to} 
      className="text-xs md:text-sm text-primary-foreground/70 hover:text-secondary transition-colors inline-flex items-center gap-1 group"
    >
      <span className="w-0 h-px bg-secondary transition-all duration-300 group-hover:w-3" />
      {children}
    </Link>
  </li>
);

export default Footer;
